import { useEffect, useState, useCallback } from "react";
import { api, wordpressApi, getToken, clearToken } from "../lib/api";
import { toast, Toaster } from "sonner";
import { MaskedLines } from "../components/Reveal";
import AdminLogin from "../components/AdminLogin";
import { Plus, Trash2, Pencil, X, ExternalLink, FileText, GraduationCap, Briefcase, LogOut } from "lucide-react";

const TABS = [
  { id: "blog", label: "Blog", icon: FileText },
  { id: "course", label: "Course", icon: GraduationCap },
  { id: "portfolio", label: "Portfolio", icon: Briefcase },
];

const ENDPOINT = { blog: "/blog", course: "/courses", portfolio: "/portfolio" };

export default function Admin() {
  const [tab, setTab] = useState("blog");
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const logout = () => { clearToken(); setAuthed(false); toast.success("Signed out"); };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center bg-brand-bg text-zinc-500">Checking session…</div>;
  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      <section className="relative pt-40 lg:pt-52 pb-16 px-6 lg:px-12 bg-brand-bg overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="max-w-[1600px] mx-auto relative flex items-start justify-between gap-6">
          <div>
            <p className="eyebrow mb-6">Studio Admin</p>
            <h1 className="font-display text-[10vw] lg:text-[7vw] leading-[0.88] tracking-tighter uppercase">
              <MaskedLines lines={[<>Content <span className="font-serif-accent text-brand-mint lowercase">console.</span></>]} />
            </h1>
            <p className="mt-6 text-zinc-400 max-w-xl">Create, edit, and delete blog posts, courses, and portfolio case studies.</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-ghost text-sm mt-2 shrink-0"><LogOut size={14} /> Sign out</button>
        </div>
      </section>

      <section className="bg-brand-bg py-8 px-6 lg:px-12 border-t border-[color:var(--border)]">
        <div className="max-w-[1600px] mx-auto flex flex-wrap gap-3" data-testid="admin-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border transition-colors duration-300 text-sm uppercase tracking-[0.2em] ${tab === t.id ? "border-brand-mint text-brand-mint" : "border-[color:var(--border)] text-zinc-400 hover:text-white"}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-brand-bg pt-8 pb-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          {tab === "blog" && <Manager kind="blog" />}
          {tab === "course" && <Manager kind="course" />}
          {tab === "portfolio" && <Manager kind="portfolio" />}
        </div>
      </section>
    </>
  );
}

/* ---------- Generic manager: list + form + edit/delete ---------- */
function Manager({ kind }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

const load = useCallback(async () => {
  setLoading(true);

  try {
   const request =
  kind === "blog"
    ? wordpressApi.get("", {
        params: {
          endpoint: "posts",
          context: "edit",
          _embed: true,
          categories_exclude: 49,
        },
      })
    : kind === "course"
    ? wordpressApi.get("", {
        params: {
          endpoint: "posts",
          context: "edit",
          categories: 49,
          per_page: 100,
          _embed: true,
        },
      })
    : api.get(ENDPOINT[kind]);

    const r = await request;

    if (kind === "blog") {

      const tagsResponse = await wordpressApi.get("", {
        params: {
          endpoint: "tags",
          per_page: 100,
        },
      });

      const tagMap = {};

      tagsResponse.data.forEach((tag) => {
        tagMap[tag.id] = tag.name;
      });

      const posts = r.data.map((post) => ({
        id: String(post.id),
        title: post.title?.rendered || "",
        slug: post.slug || "",
        excerpt: post.excerpt?.raw || "",
        content: post.content?.rendered || "",
        cover_image:
          post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
        author: "Dorado9 Team",
        tags: (post.tags || [])
          .map((tagId) => tagMap[tagId])
          .filter(Boolean),
        published_at: post.date || "",
        wordpress_id: post.id,
        featured_media: post.featured_media || 0,
      }));

      setItems(posts);

   } else if (kind === "course") {
  const courses = (r.data || []).map((post) => ({
    id: String(post.id),
    wordpress_id: post.id,

    title: post.title?.rendered || "",
    slug: post.slug || "",

    description:
      post.content?.rendered ||
      post.excerpt?.rendered ||
      "",

    cover_image:
      post._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",

    featured_media: post.featured_media || 0,

    price: post.meta?.course_price || "",
    duration: post.meta?.course_duration || "",
    level: post.meta?.course_level || "Beginner",

    instructor:
      post.meta?.course_instructor || "Dorado9 Team",

    syllabus_url:
      post.meta?.course_syllabus_url || "",

    modules:
      post.meta?.course_modules || [],

    curriculum:
      post.meta?.course_curriculum || [],

    tools:
      post.meta?.course_tools || [],

    why_choose_intro:
      post.meta?.course_why_choose_intro || "",

    why_choose:
      post.meta?.course_why_choose || [],

    student_work:
      post.meta?.course_student_work || [],

    faqs:
      post.meta?.course_faqs || [],
  }));

  setItems(courses);

} else {
  setItems(r.data);
}

  } catch (error) {
    console.error("Load error:", error);

    if (kind === "blog") {
      toast.error("Could not load WordPress blogs");
    }

if (kind === "course") {

  toast.error("Could not load WordPress courses");

}
    
  } finally {
    setLoading(false);
  }
}, [kind]);

  useEffect(() => { load(); setEditing(null); }, [load]);

  const del = async (item) => {
  if (!window.confirm(`Delete "${item.title}"? This can't be undone.`)) return;

  try {
    if (kind === "blog" || kind === "course") {

  await wordpressApi.delete("", {

    params: {

      endpoint: `posts/${item.wordpress_id || item.id}`,

    },

  });

} else {

  await api.delete(`${ENDPOINT[kind]}/${item.id}`);

}

    toast.success("Deleted");

    if (editing?.id === item.id) {
      setEditing(null);
    }

    load();
  } catch (error) {
    console.error("Delete error:", error.response?.data || error);
    toast.error("Delete failed");
  }
};

  const Form = { blog: BlogForm, course: CourseForm, portfolio: PortfolioForm }[kind];

  return (
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-6">
        <Form kind={kind} editing={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      </div>
      <div className="lg:col-span-6">
        <div className="border border-[color:var(--border)] p-6 lg:p-8 bg-[color:var(--surface)]">
          <div className="flex items-center justify-between mb-6">
            <p className="eyebrow">Existing · {items.length}</p>
            <button onClick={load} className="text-xs uppercase tracking-[0.24em] text-zinc-400 hover:text-brand-mint transition-colors" data-testid={`admin-${kind}-refresh`}>Refresh</button>
          </div>
          {loading && <p className="text-zinc-500">Loading…</p>}
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} className={`flex items-center gap-4 p-3 border rounded-lg transition-colors ${editing?.id === it.id ? "border-brand-mint" : "border-[color:var(--border)]"}`} data-testid={`admin-${kind}-item-${it.slug}`}>
                <div className="w-14 h-14 flex-none overflow-hidden rounded bg-black/40">
                  <img src={it.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-base truncate">{it.title}</p>
                  <p className="text-xs text-zinc-500 truncate mt-0.5">/{it.slug}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={`${ENDPOINT[kind]}/${it.slug}`} target="_blank" rel="noreferrer" title="View" className="p-2 text-zinc-500 hover:text-white transition-colors"><ExternalLink size={15} /></a>
                  <button onClick={() => { setEditing(it); window.scrollTo({ top: 200, behavior: "smooth" }); }} title="Edit" data-testid={`admin-edit-${kind}-${it.slug}`} className="p-2 text-zinc-500 hover:text-brand-mint transition-colors"><Pencil size={15} /></button>
                  <button onClick={() => del(it)} title="Delete" data-testid={`admin-delete-${kind}-${it.slug}`} className="p-2 text-zinc-500 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {!loading && items.length === 0 && <p className="text-zinc-500">No entries yet. Create one on the left.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Shared UI ---------- */
function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
}

function Input({ label, value, onChange, testid, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-brand-mint outline-none py-3 text-white transition-colors" />
    </div>
  );
}

function Area({ label, value, onChange, testid, rows = 4, placeholder = "" }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="w-full bg-transparent border border-[color:var(--border)] focus:border-brand-mint outline-none p-3 text-white transition-colors resize-none" />
    </div>
  );
}

// Dynamic list of objects
function Repeater({ label, items, onChange, fields, addLabel, testid }) {
  const blank = () => Object.fromEntries(fields.map((f) => [f.key, ""]));
  const add = () => onChange([...(items || []), blank()]);
  const upd = (i, key, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const rm = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="border border-[color:var(--border)] rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <label className="eyebrow">{label}</label>
        <button type="button" onClick={add} className="text-xs uppercase tracking-[0.2em] text-brand-mint hover:text-brand-mint/80 inline-flex items-center gap-1" data-testid={`${testid}-add`}><Plus size={13} /> {addLabel}</button>
      </div>
      <div className="space-y-4">
        {(items || []).map((it, i) => (
          <div key={i} className="relative border border-[color:var(--border)] rounded-md p-3 pt-3 bg-black/20" data-testid={`${testid}-row-${i}`}>
            <button type="button" onClick={() => rm(i)} className="absolute top-2 right-2 text-zinc-500 hover:text-red-400" data-testid={`${testid}-remove-${i}`}><X size={14} /></button>
            <div className="space-y-3 pr-6">
              {fields.map((f) => f.area ? (
                <textarea key={f.key} rows={2} value={it[f.key] || ""} onChange={(e) => upd(i, f.key, e.target.value)} placeholder={f.label}
                  className="w-full bg-transparent border border-[color:var(--border)] focus:border-brand-mint outline-none p-2 text-white text-sm transition-colors resize-none" />
              ) : (
                <input key={f.key} value={it[f.key] || ""} onChange={(e) => upd(i, f.key, e.target.value)} placeholder={f.label}
                  className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-brand-mint outline-none py-2 text-white text-sm transition-colors" />
              ))}
            </div>
          </div>
        ))}
        {(!items || items.length === 0) && <p className="text-zinc-600 text-xs">None yet — click "{addLabel}".</p>}
      </div>
    </div>
  );
}

function FormShell({ title, kind, editing, onSubmit, onCancel, busy, children }) {
  return (
    <form onSubmit={onSubmit} className="border border-[color:var(--border)] p-6 lg:p-8 bg-[color:var(--surface)] space-y-5" data-testid={`admin-form-${kind}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="eyebrow">{editing ? "Edit Entry" : "New Entry"}</p>
          <h2 className="font-display text-2xl lg:text-3xl mt-2 uppercase">{title}</h2>
        </div>
        {editing ? <Pencil size={22} className="text-brand-mint" /> : <Plus size={24} className="text-brand-mint" />}
      </div>
      {children}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid={`admin-submit-${kind}`}>
          {busy ? "Saving…" : editing ? "Update" : "Publish"}
        </button>
        {editing && <button type="button" onClick={onCancel} className="btn-ghost" data-testid="admin-cancel-edit">Cancel</button>}
      </div>
    </form>
  );
}

const csvToArr = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);
const linesToArr = (s) => (s ? s.split("\n").map((x) => x.trim()).filter(Boolean) : []);

/* ---------- Blog ---------- */

  const blogEmpty = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "Dorado9 Team",
  tags: ""
};
function BlogForm({ editing, onSaved, onCancel }) {
  const [f, setF] = useState(blogEmpty);
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setF({ ...blogEmpty, ...editing, tags: (editing.tags || []).join(", ") });
      setCoverFile(null);
    } else {
      setF(blogEmpty);
      setCoverFile(null);
    }
  }, [editing]);


async function getOrCreateTagIds(tagString) {
  const tagNames = (tagString || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

  const tagIds = [];

  for (const name of tagNames) {
    const slug = slugify(name);

    // Check whether the tag already exists
    const existing = await wordpressApi.get("", {
      params: {
        endpoint: "tags",
        slug: slug
      }
    });

    if (existing.data && existing.data.length > 0) {
      tagIds.push(existing.data[0].id);
      continue;
    }

    // Create the tag if it doesn't exist
    const created = await wordpressApi.post(
      "",
      {
        name: name,
        slug: slug
      },
      {
        params: {
          endpoint: "tags"
        }
      }
    );

    tagIds.push(created.data.id);
  }

  return tagIds;
}

  async function uploadCoverImage() {
    if (!coverFile) return null;

    const formData = new FormData();
    formData.append("file", coverFile);

    console.log("Uploading file:", {
      name: coverFile.name,
      type: coverFile.type,
      size: coverFile.size,
    });

    try {
      const response = await wordpressApi.post("", formData, {
        params: {
          endpoint: "media",
        },
        timeout: 60000,
      });

      console.log("MEDIA UPLOAD RESPONSE:", response.status, response.data);

      if (!response.data?.id) {
        throw new Error("WordPress did not return a media ID");
      }

      return response.data.id;
    } catch (error) {
      console.error(
        "MEDIA UPLOAD ERROR:",
        error.response?.status,
        error.response?.data || error.message
      );

      throw error;
    }
  }

  const submit = async (e) => {
  e.preventDefault();

  if (!f.title || !f.excerpt || !f.content) {
    return toast.error("Fill required fields");
  }

  if (!editing && !coverFile) {
    return toast.error("Please select a cover image");
  }

  setBusy(true);
  try {
    const tagIds = await getOrCreateTagIds(f.tags);
    let featuredMediaId = f.featured_media || 0;

    if (coverFile) {
      toast.info("Uploading cover image...");
      featuredMediaId = await uploadCoverImage();
    }

    const payload = {
      title: f.title,
      slug: f.slug || slugify(f.title),
      excerpt: f.excerpt,
      content: f.content,
      status: "publish",
      tags: tagIds,
    };

    if (featuredMediaId) {
      payload.featured_media = featuredMediaId;
    }

  if (editing) {

    console.log("Updating WordPress post:", editing.wordpress_id);
    console.log("Payload:", payload);

    const response = await wordpressApi.post("", payload, {
      params: {
        endpoint: `posts/${editing.wordpress_id}`,
      },
    });

    console.log("WordPress response status:", response.status);
    console.log("WordPress response:", response.data);

    toast.success("Blog updated");

  } else {

    const response = await wordpressApi.post("", payload, {
      params: {
        endpoint: "posts",
      },
    });

    console.log("WordPress create response:", response.data);

    toast.success("Blog published");
  }

  onSaved();

} catch (error) {
  console.error("WordPress save error:", error.response?.data || error);

  toast.error(
    error.response?.data?.message ||
    error.response?.data?.error ||
    "Save failed"
  );

} finally {
  setBusy(false);
}
  };

  return (
    <FormShell title="Blog Post" kind="blog" editing={editing} onSubmit={submit} onCancel={onCancel} busy={busy}>
      <Input label="Title *" value={f.title} onChange={(v) => setF({ ...f, title: v, slug: editing ? f.slug : slugify(v) })} testid="admin-blog-title" />
      <Input label="Slug" value={f.slug} onChange={(v) => setF({ ...f, slug: v })} testid="admin-blog-slug" placeholder="auto" />
      <div className="space-y-2">
        <label className="block text-sm text-zinc-400">Cover Image</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-zinc-400 file:mr-4 file:rounded-lg file:border-0 file:bg-brand-purple file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:opacity-90"
          data-testid="admin-blog-cover"
        />
        {f.cover_image && !coverFile && (
          <div className="mt-3">
            <p className="text-xs text-zinc-500 mb-2">Current cover image</p>
            <img src={f.cover_image} alt="Current cover" className="w-48 aspect-video object-cover border border-zinc-800" />
          </div>
        )}
        {coverFile && <p className="text-xs text-brand-mint">Selected: {coverFile.name}</p>}
      </div>
      <Area label="Excerpt *" value={f.excerpt} onChange={(v) => setF({ ...f, excerpt: v })} testid="admin-blog-excerpt" rows={2} />
      <Area label="Content (HTML) *" value={f.content} onChange={(v) => setF({ ...f, content: v })} testid="admin-blog-content" rows={8} placeholder="<p>…</p>" />
      <Input label="Tags (comma separated)" value={f.tags} onChange={(v) => setF({ ...f, tags: v })} testid="admin-blog-tags" />
    </FormShell>
  );
}

/* ---------- Course ---------- */
const courseEmpty = {
  title: "", slug: "", description: "", duration: "", level: "Beginner", price: "", cover_image: "", instructor: "Dorado9 Team",
  syllabus_url: "", why_choose_intro: "", curriculum: [], tools: [], faqs: [], why_choose: [], student_work: [],
};
function CourseForm({ editing, onSaved, onCancel }) {
  const [f, setF] = useState(courseEmpty);
  const [coverFile, setCoverFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      const curriculum =
        editing.curriculum && editing.curriculum.length
          ? editing.curriculum
          : (editing.modules || []).map((m) => ({
              title: m,
              details: "",
            }));

      setF({
        ...courseEmpty,
        ...editing,
        curriculum,
      });

      setCoverFile(null);
    } else {
      setF(courseEmpty);
      setCoverFile(null);
    }
  }, [editing]);

  async function uploadCourseCover() {
    if (!coverFile) return null;

    const formData = new FormData();
    formData.append("file", coverFile);

    try {
      toast.info("Uploading course cover...");

      const response = await wordpressApi.post(
        "",
        formData,
        {
          params: {
            endpoint: "media",
          },
          timeout: 60000,
        }
      );

      if (!response.data?.id) {
        throw new Error("WordPress did not return a media ID");
      }

      return response.data.id;

    } catch (error) {
      console.error(
        "Course media upload error:",
        error.response?.data || error
      );

      throw error;
    }
  }

  const submit = async (e) => {
    e.preventDefault();

    if (
      !f.title ||
      !f.description ||
      !f.duration ||
      !f.price
    ) {
      return toast.error("Fill required fields");
    }

    if (!editing && !coverFile) {
      return toast.error("Please select a course cover image");
    }

    setBusy(true);

    try {
      let featuredMediaId = f.featured_media || 0;

      if (coverFile) {
        featuredMediaId = await uploadCourseCover();
      }

      const curriculum = (f.curriculum || []).filter(
        (m) => m.title
      );

      const modules = curriculum.map(
        (m) => m.title
      );

      const tools = (f.tools || []).filter(
        (t) => t.name
      );

      const faqs = (f.faqs || []).filter(
        (x) => x.q
      );

      const whyChoose = (f.why_choose || []).filter(
        (w) => w.heading
      );

      const studentWork = (f.student_work || []).filter(
        (s) => s.image
      );

      const slug =
        f.slug || slugify(f.title);

      const payload = {
        title: f.title,
        slug,

        content: f.description,
        excerpt: f.description,

        status: "publish",

        categories: [49],

        meta: {
          course_price: f.price,
          course_duration: f.duration,
          course_level: f.level,
          course_instructor:
            f.instructor || "Dorado9 Team",

          course_syllabus_url:
            f.syllabus_url,

          course_modules: modules,

          course_curriculum:
            curriculum,

          course_tools:
            tools,

          course_why_choose_intro:
            f.why_choose_intro,

          course_why_choose:
            whyChoose,

          course_student_work:
            studentWork,

          course_faqs:
            faqs,
        },
      };

      if (featuredMediaId) {
        payload.featured_media =
          featuredMediaId;
      }

      console.log(
        "Saving WordPress course:",
        payload
      );

      if (editing) {
        await wordpressApi.post(
          "",
          payload,
          {
            params: {
              endpoint: `posts/${
                editing.wordpress_id || editing.id
              }`,
            },
          }
        );

        toast.success("Course updated");

      } else {
        await wordpressApi.post(
          "",
          payload,
          {
            params: {
              endpoint: "posts",
            },
          }
        );

        toast.success("Course published");
      }

      setCoverFile(null);
      onSaved();

    } catch (error) {
      console.error(
        "WordPress course save error:",
        error.response?.data || error
      );

      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Course save failed"
      );

    } finally {
      setBusy(false);
    }
  };

  return (
    <FormShell
      title="Course"
      kind="course"
      editing={editing}
      onSubmit={submit}
      onCancel={onCancel}
      busy={busy}
    >

      <Input
        label="Title *"
        value={f.title}
        onChange={(v) =>
          setF({
            ...f,
            title: v,
            slug: editing
              ? f.slug
              : slugify(v),
          })
        }
        testid="admin-course-title"
      />

      <Input
        label="Slug"
        value={f.slug}
        onChange={(v) =>
          setF({
            ...f,
            slug: v,
          })
        }
        testid="admin-course-slug"
        placeholder="auto"
      />

      <div className="space-y-2">
        <label className="block text-sm text-zinc-400">
          Course Cover Image *
        </label>

        <input
          type="file"
          accept="image/*"
          onChange={(e) =>
            setCoverFile(
              e.target.files?.[0] || null
            )
          }
          className="block w-full text-sm text-zinc-400 file:mr-4 file:rounded-lg file:border-0 file:bg-brand-purple file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:opacity-90"
          data-testid="admin-course-cover"
        />

        {f.cover_image && !coverFile && (
          <div className="mt-3">
            <p className="text-xs text-zinc-500 mb-2">
              Current cover image
            </p>

            <img
              src={f.cover_image}
              alt="Current course cover"
              className="w-48 aspect-video object-cover border border-zinc-800"
            />
          </div>
        )}

        {coverFile && (
          <p className="text-xs text-brand-mint">
            Selected: {coverFile.name}
          </p>
        )}
      </div>

      <Area
        label="Description *"
        value={f.description}
        onChange={(v) =>
          setF({
            ...f,
            description: v,
          })
        }
        testid="admin-course-description"
        rows={5}
      />

      <div className="grid grid-cols-3 gap-4">

        <Input
          label="Duration *"
          value={f.duration}
          onChange={(v) =>
            setF({
              ...f,
              duration: v,
            })
          }
          testid="admin-course-duration"
          placeholder="6 weeks"
        />

        <div>
          <label className="eyebrow block mb-2">
            Level
          </label>

          <select
            value={f.level}
            onChange={(e) =>
              setF({
                ...f,
                level: e.target.value,
              })
            }
            data-testid="admin-course-level"
            className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-brand-mint outline-none py-3 text-white transition-colors"
          >
            <option className="bg-brand-bg">
              Beginner
            </option>

            <option className="bg-brand-bg">
              Intermediate
            </option>

            <option className="bg-brand-bg">
              Advanced
            </option>
          </select>
        </div>

        <Input
          label="Price *"
          value={f.price}
          onChange={(v) =>
            setF({
              ...f,
              price: v,
            })
          }
          testid="admin-course-price"
          placeholder="₹9,999"
        />

      </div>

      <Input
        label="Instructor"
        value={f.instructor}
        onChange={(v) =>
          setF({
            ...f,
            instructor: v,
          })
        }
        testid="admin-course-instructor"
      />

      <Input
        label="Syllabus Download URL"
        value={f.syllabus_url}
        onChange={(v) =>
          setF({
            ...f,
            syllabus_url: v,
          })
        }
        testid="admin-course-syllabus"
        placeholder="https://…/syllabus.pdf"
      />

      <Repeater
        label="Curriculum Modules (accordion)"
        testid="admin-course-curriculum"
        addLabel="Add module"
        items={f.curriculum}
        onChange={(v) =>
          setF({
            ...f,
            curriculum: v,
          })
        }
        fields={[
          {
            key: "title",
            label: "Module title",
          },
          {
            key: "details",
            label: "Module details",
            area: true,
          },
        ]}
      />

      <Repeater
        label="Tools You'll Master"
        testid="admin-course-tools"
        addLabel="Add tool"
        items={f.tools}
        onChange={(v) =>
          setF({
            ...f,
            tools: v,
          })
        }
        fields={[
          {
            key: "name",
            label: "Tool name",
          },
          {
            key: "logo",
            label: "Logo image URL",
          },
        ]}
      />

      <Area
        label="Why Choose — intro paragraph"
        value={f.why_choose_intro}
        onChange={(v) =>
          setF({
            ...f,
            why_choose_intro: v,
          })
        }
        testid="admin-course-why-intro"
        rows={3}
      />

      <Repeater
        label="Why Choose — tiles"
        testid="admin-course-why"
        addLabel="Add tile"
        items={f.why_choose}
        onChange={(v) =>
          setF({
            ...f,
            why_choose: v,
          })
        }
        fields={[
          {
            key: "heading",
            label: "Tile heading",
          },
          {
            key: "description",
            label: "Description",
            area: true,
          },
          {
            key: "image",
            label: "Image URL (optional)",
          },
        ]}
      />

      <Repeater
        label="Student Portfolio"
        testid="admin-course-students"
        addLabel="Add work"
        items={f.student_work}
        onChange={(v) =>
          setF({
            ...f,
            student_work: v,
          })
        }
        fields={[
          {
            key: "image",
            label: "Image URL",
          },
          {
            key: "description",
            label: "Short description",
            area: true,
          },
        ]}
      />

      <Repeater
        label="FAQs"
        testid="admin-course-faqs"
        addLabel="Add FAQ"
        items={f.faqs}
        onChange={(v) =>
          setF({
            ...f,
            faqs: v,
          })
        }
        fields={[
          {
            key: "q",
            label: "Question",
          },
          {
            key: "a",
            label: "Answer",
            area: true,
          },
        ]}
      />

    </FormShell>
  );
}
/* ---------- Portfolio ---------- */
const pfEmpty = { title: "", slug: "", client: "", category: "", summary: "", cover_image: "", year: String(new Date().getFullYear()), services: "", metrics: "" };
function PortfolioForm({ editing, onSaved, onCancel }) {
  const [f, setF] = useState(pfEmpty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) setF({ ...pfEmpty, ...editing, services: (editing.services || []).join(", "), metrics: (editing.metrics || []).join("\n") });
    else setF(pfEmpty);
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.client || !f.summary || !f.cover_image) return toast.error("Fill required fields");
    setBusy(true);
    const payload = { ...f, slug: f.slug || slugify(f.title), services: csvToArr(f.services), metrics: linesToArr(f.metrics) };
    try {
      if (editing) await api.put(`/portfolio/${editing.id}`, payload);
      else await api.post("/portfolio", payload);
      toast.success(editing ? "Project updated" : "Project published");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <FormShell title="Portfolio Project" kind="portfolio" editing={editing} onSubmit={submit} onCancel={onCancel} busy={busy}>
      <Input label="Title *" value={f.title} onChange={(v) => setF({ ...f, title: v, slug: editing ? f.slug : slugify(v) })} testid="admin-portfolio-title" />
      <Input label="Slug" value={f.slug} onChange={(v) => setF({ ...f, slug: v })} testid="admin-portfolio-slug" placeholder="auto" />
      <Input label="Cover Image URL *" value={f.cover_image} onChange={(v) => setF({ ...f, cover_image: v })} testid="admin-portfolio-cover" />
      <div className="grid grid-cols-2 gap-4">
        <Input label="Client *" value={f.client} onChange={(v) => setF({ ...f, client: v })} testid="admin-portfolio-client" />
        <Input label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="admin-portfolio-category" />
      </div>
      <Input label="Year" value={f.year} onChange={(v) => setF({ ...f, year: v })} testid="admin-portfolio-year" />
      <Area label="Summary *" value={f.summary} onChange={(v) => setF({ ...f, summary: v })} testid="admin-portfolio-summary" rows={3} />
      <Input label="Services (comma separated)" value={f.services} onChange={(v) => setF({ ...f, services: v })} testid="admin-portfolio-services" />
      <Area label="Metrics (one per line)" value={f.metrics} onChange={(v) => setF({ ...f, metrics: v })} testid="admin-portfolio-metrics" rows={3} placeholder="+320% Traffic&#10;5x ROAS" />
    </FormShell>
  );
}
